import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  cardHeaderClass,
  cardTitleClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  btnPrimaryClass,
  btnSecondaryClass,
  btnDangerClass,
  alertErrorClass,
  alertInfoClass,
  spinnerClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
  tableActionsCellClass,
} from "../../dashboardStyles";
import {
  GetPlatformPricingAction,
  SetPlanPriceAction,
  DeleteScheduledPriceAction,
  CreatePromotionAction,
  UpdatePromotionAction,
  DeletePromotionAction,
  SetTrialDaysAction,
} from "../../../../store/action";
import {
  SubscriptionPlan,
  PlanNameKeys,
  formatMoney,
  formatPrice,
} from "../../../../constants/subscription";
import { notify } from "../../../../utils/notify";

/** Colour per promotion status. The word itself is computed server-side. */
const STATUS_BADGE = {
  live: "bg-brand-50 text-brand-700",
  scheduled: "bg-amber-50 text-amber-700",
  ended: "bg-ink-100 text-ink-500",
  disabled: "bg-ink-100 text-ink-500",
};

/**
 * The plans whose price can actually be set, mirroring
 * `PlanPricing.AdjustablePlans` on the backend — which is fewer than the plans
 * that carry a number.
 *
 * Start is absent because free is not a price it happens to have: it is where
 * every lapsed subscription lands, and there is nothing below it to fall to, so
 * a price on it would break the entitlement model rather than merely be odd.
 * Business is absent because it is retired — companies still on it hold a
 * locked price, so changing its list price moves nobody, and a control that
 * does nothing is worse than no control.
 *
 * Both are still quoted elsewhere: Start's zero on the public grid, Business's
 * number wherever a company on it is listed. Both also keep their rows in the
 * price history below, which is a record and not an editor.
 *
 * A plan missing from this list cannot be priced or discounted from this tab at
 * all — the failure to watch for when a tier is added.
 */
const PRICED_PLANS = [SubscriptionPlan.ESSENTIAL, SubscriptionPlan.PROFESSIONAL];

/**
 * A datetime-local input wants "YYYY-MM-DDTHH:mm" in local time; the API speaks
 * UTC ISO. These two convert between them, and the offset correction is the
 * whole reason they exist — `toISOString().slice(0, 16)` silently shows a
 * Bucharest owner a sale starting three hours earlier than they set it.
 */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fromLocalInput = (value) =>
  value ? new Date(value).toISOString() : null;

/** Midnight tonight, local — the natural start for a sale set up today. */
const defaultStart = () => {
  const start = new Date();
  start.setHours(start.getHours() + 1, 0, 0, 0);
  return toLocalInput(start.toISOString());
};

/** Twenty-four hours after the start, which is what "Black Friday" means here. */
const defaultEnd = () => {
  const end = new Date();
  end.setHours(end.getHours() + 25, 0, 0, 0);
  return toLocalInput(end.toISOString());
};

const emptyPromotion = () => ({
  id: null,
  name: "",
  discountPercent: 50,
  plan: "",
  startsAt: defaultStart(),
  endsAt: defaultEnd(),
  isActive: true,
});

/**
 * What EasyPlanner charges, and what is on offer.
 *
 * SystemOwner-only, and deliberately so: an Admin looks after the companies
 * assigned to them, but the price list belongs to the platform. See
 * `constants/navigation.js` for why the tab and its URL are held back together,
 * and `PlatformPricingController` for the server-side half.
 *
 * Two ideas, kept visibly apart because they behave differently:
 *
 *  - A **price** is versioned. Setting one adds a row; it never edits the old
 *    one. Businesses already on the plan keep what they were sold at, which is
 *    why the history below is worth showing at all — it is the record that
 *    proves nobody was moved.
 *  - A **promotion** is a window. It discounts whoever arrives while it runs,
 *    and the price they lock in outlives it.
 */
const PlatformPricing = () => {
  const { t, i18n } = useTranslation();

  const [current, setCurrent] = useState([]);
  const [history, setHistory] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [busyId, setBusyId] = useState(null);

  // Price editor: which plan is open, and the values typed into it.
  const [editingPlan, setEditingPlan] = useState(null);
  const [priceValue, setPriceValue] = useState("");
  const [priceEffectiveFrom, setPriceEffectiveFrom] = useState("");
  const [priceNote, setPriceNote] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);

  const [promotionForm, setPromotionForm] = useState(null);
  const [savingPromotion, setSavingPromotion] = useState(false);

  // Held as a string, not a number: an empty field while someone is retyping is
  // a legitimate intermediate state, and coercing it to 0 on every keystroke
  // would make the "no trial" warning flash as they delete the old value.
  const [trialDays, setTrialDays] = useState("");
  const [savingTrial, setSavingTrial] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await GetPlatformPricingAction();
      setCurrent(Array.isArray(data?.current) ? data.current : []);
      setHistory(Array.isArray(data?.history) ? data.history : []);
      setPromotions(Array.isArray(data?.promotions) ? data.promotions : []);
      setTrialDays(
        data?.trialDays == null ? "" : String(data.trialDays),
      );
    } catch (err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** The server's own reason when it gave one, else a generic per-action message. */
  const messageFor = (err, action) => {
    const code = err?.response?.data?.code;
    return code
      ? t(`Dashboard.Platform.pricing.errors.${code}`, {
          defaultValue: t(`Dashboard.Platform.pricing.errors.${action}`),
        })
      : t(`Dashboard.Platform.pricing.errors.${action}`);
  };

  // Every coded refusal here is a rule (out of range, already in effect, not a
  // priced plan): yellow. Only an uncoded failure — network, 500 — is red.
  const reportFailure = (err, action) => {
    const message = messageFor(err, action);
    if (err?.response?.data?.code) notify.warning(message);
    else notify.error(message);
  };

  const quoteFor = (plan) => current.find((q) => q.plan === plan);

  const openPriceEditor = (plan) => {
    const quote = quoteFor(plan);
    setEditingPlan(plan);
    setPriceValue(quote?.listPrice != null ? String(quote.listPrice) : "0");
    setPriceEffectiveFrom("");
    setPriceNote("");
  };

  const submitPrice = async (e) => {
    e.preventDefault();
    setSavingPrice(true);
    try {
      await SetPlanPriceAction({
        plan: editingPlan,
        price: Number(priceValue),
        effectiveFrom: fromLocalInput(priceEffectiveFrom),
        note: priceNote.trim() || null,
      });
      setEditingPlan(null);
      notify.success(t("Dashboard.Platform.pricing.priceSaved"));
      await load();
    } catch (err) {
      reportFailure(err, "savePrice");
    } finally {
      setSavingPrice(false);
    }
  };

  const submitTrialDays = async (e) => {
    e.preventDefault();
    setSavingTrial(true);
    try {
      await SetTrialDaysAction(Number(trialDays));
      notify.success(t("Dashboard.Platform.pricing.trialSaved"));
      await load();
    } catch (err) {
      reportFailure(err, "saveTrialDays");
    } finally {
      setSavingTrial(false);
    }
  };

  const withdrawPrice = async (version) => {
    setBusyId(`price-${version.id}`);
    try {
      await DeleteScheduledPriceAction(version.id);
      notify.success(t("Dashboard.Platform.pricing.priceWithdrawn"));
      await load();
    } catch (err) {
      reportFailure(err, "withdrawPrice");
    } finally {
      setBusyId(null);
    }
  };

  const openPromotionEditor = (promotion) => {
    setPromotionForm(
      promotion
        ? {
            id: promotion.id,
            name: promotion.name,
            discountPercent: promotion.discountPercent,
            plan: promotion.plan == null ? "" : String(promotion.plan),
            startsAt: toLocalInput(promotion.startsAt),
            endsAt: toLocalInput(promotion.endsAt),
            isActive: promotion.isActive,
          }
        : emptyPromotion(),
    );
  };

  const submitPromotion = async (e) => {
    e.preventDefault();
    setSavingPromotion(true);

    const request = {
      name: promotionForm.name.trim(),
      discountPercent: Number(promotionForm.discountPercent),
      plan: promotionForm.plan === "" ? null : Number(promotionForm.plan),
      startsAt: fromLocalInput(promotionForm.startsAt),
      endsAt: fromLocalInput(promotionForm.endsAt),
      isActive: promotionForm.isActive,
    };

    try {
      if (promotionForm.id) {
        await UpdatePromotionAction(promotionForm.id, request);
      } else {
        await CreatePromotionAction(request);
      }
      setPromotionForm(null);
      notify.success(t("Dashboard.Platform.pricing.promotionSaved"));
      await load();
    } catch (err) {
      reportFailure(err, "savePromotion");
    } finally {
      setSavingPromotion(false);
    }
  };

  /**
   * The off switch. Kept as a one-click toggle rather than something buried in
   * the editor, because the moment it is needed — a sale discounting far more
   * than intended — is the moment nobody wants to open a form.
   */
  const togglePromotion = async (promotion) => {
    setBusyId(`promo-${promotion.id}`);
    try {
      await UpdatePromotionAction(promotion.id, {
        name: promotion.name,
        discountPercent: promotion.discountPercent,
        plan: promotion.plan,
        startsAt: promotion.startsAt,
        endsAt: promotion.endsAt,
        isActive: !promotion.isActive,
      });
      notify.success(
        t(
          promotion.isActive
            ? "Dashboard.Platform.pricing.promotionTurnedOff"
            : "Dashboard.Platform.pricing.promotionTurnedOn",
        ),
      );
      await load();
    } catch (err) {
      reportFailure(err, "savePromotion");
    } finally {
      setBusyId(null);
    }
  };

  const removePromotion = async (promotion) => {
    setBusyId(`promo-${promotion.id}`);
    try {
      await DeletePromotionAction(promotion.id);
      notify.success(t("Dashboard.Platform.pricing.promotionDeleted"));
      await load();
    } catch (err) {
      reportFailure(err, "deletePromotion");
    } finally {
      setBusyId(null);
    }
  };

  const formatDateTime = (value) =>
    value ? new Date(value).toLocaleString(i18n.language) : "—";

  const planName = (plan) =>
    plan == null
      ? t("Dashboard.Platform.pricing.allPlans")
      : t(PlanNameKeys[plan], { defaultValue: String(plan) });

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>
          {t("Dashboard.Platform.pricing.title")}
        </h1>
        <p className={pageSubtitleClass}>
          {t("Dashboard.Platform.pricing.subtitle")}
        </p>
      </div>

      {failed && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{t("Dashboard.Platform.pricing.loadError")}</span>
        </div>
      )}

      {loading ? (
        <div className={spinnerClass} role="status" aria-live="polite">
          <span className="sr-only">{t("Dashboard.Menu.loading")}</span>
        </div>
      ) : (
        <>
          {/* --- Current prices ------------------------------------------- */}
          <div className={`${cardClass} mb-6`}>
            <div className={cardHeaderClass}>
              <h2 className={cardTitleClass}>
                {t("Dashboard.Platform.pricing.currentTitle")}
              </h2>
            </div>
            <div className={cardBodyClass}>
              <p className={`${alertInfoClass} mb-5`}>
                <i className="bi bi-info-circle" aria-hidden="true" />
                <span>{t("Dashboard.Platform.pricing.grandfatherNote")}</span>
              </p>

              <div className="grid gap-4 sm:grid-cols-3">
                {PRICED_PLANS.map((plan) => {
                  const quote = quoteFor(plan);
                  const discounted =
                    quote?.discountPercent > 0 &&
                    quote?.listPrice != null &&
                    quote.listPrice > 0;

                  return (
                    <div
                      key={plan}
                      className="rounded-card border border-ink-200 p-4"
                    >
                      <h3 className="m-0 text-sm font-semibold uppercase tracking-wide text-brand-700">
                        {planName(plan)}
                      </h3>

                      <p className="mt-2 flex flex-wrap items-baseline gap-2">
                        {discounted && (
                          <span className="text-base text-ink-400 line-through">
                            {formatMoney(quote.listPrice, quote?.currency)}
                          </span>
                        )}
                        <span className="text-2xl font-bold tabular-nums text-ink-900">
                          {formatMoney(quote?.price ?? 0, quote?.currency)}
                        </span>
                        <span className="text-xs text-ink-400">
                          / {t("Pricing.period")}
                        </span>
                      </p>

                      {discounted && (
                        <p className="mt-1 text-xs font-semibold text-brand-700">
                          {quote.promotionName} · −{quote.discountPercent}%
                        </p>
                      )}

                      <button
                        type="button"
                        // Keyed on the plan, not its position: the grid gained a
                        // tier once already, and every caller that found this
                        // button by index picked the wrong plan the day it did.
                        data-testid={`change-price-${plan}`}
                        className={`${btnSecondaryClass} mt-3 w-full`}
                        onClick={() => openPriceEditor(plan)}
                      >
                        <i className="bi bi-pencil" aria-hidden="true" />
                        {t("Dashboard.Platform.pricing.changePrice")}
                      </button>
                    </div>
                  );
                })}
              </div>

              {editingPlan != null && (
                <form
                  onSubmit={submitPrice}
                  className="mt-5 rounded-card border border-brand-200 bg-brand-50/40 p-4"
                >
                  <h3 className="m-0 mb-3 text-sm font-semibold text-ink-900">
                    {t("Dashboard.Platform.pricing.newPriceFor", {
                      plan: planName(editingPlan),
                    })}
                  </h3>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className={formLabelClass} htmlFor="pricing-amount">
                        {t("Dashboard.Platform.pricing.amount")}
                        <span className={requiredMarkClass}>*</span>
                      </label>
                      <input
                        id="pricing-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        className={formControlClass}
                        value={priceValue}
                        onChange={(e) => setPriceValue(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className={formLabelClass} htmlFor="pricing-from">
                        {t("Dashboard.Platform.pricing.effectiveFrom")}
                      </label>
                      <input
                        id="pricing-from"
                        type="datetime-local"
                        className={formControlClass}
                        value={priceEffectiveFrom}
                        onChange={(e) => setPriceEffectiveFrom(e.target.value)}
                      />
                      <p className="mt-1 text-xs text-ink-400">
                        {t("Dashboard.Platform.pricing.effectiveFromHint")}
                      </p>
                    </div>

                    <div>
                      <label className={formLabelClass} htmlFor="pricing-note">
                        {t("Dashboard.Platform.pricing.note")}
                      </label>
                      <input
                        id="pricing-note"
                        type="text"
                        maxLength={500}
                        className={formControlClass}
                        value={priceNote}
                        onChange={(e) => setPriceNote(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      type="submit"
                      className={btnPrimaryClass}
                      disabled={savingPrice}
                    >
                      {savingPrice
                        ? t("Dashboard.Platform.pricing.saving")
                        : t("Dashboard.Platform.pricing.savePrice")}
                    </button>
                    <button
                      type="button"
                      className={btnSecondaryClass}
                      onClick={() => setEditingPlan(null)}
                    >
                      {t("Dashboard.Platform.pricing.cancel")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* --- Free trial -------------------------------------------------
              A commercial term rather than a price, but the same person's
              decision on the same screen. Kept as its own card because it
              applies to every plan at once, unlike everything above it. */}
          <div className={`${cardClass} mb-6`}>
            <div className={cardHeaderClass}>
              <h2 className={cardTitleClass}>
                {t("Dashboard.Platform.pricing.trialTitle")}
              </h2>
            </div>

            <div className={cardBodyClass}>
              <p className={`${alertInfoClass} mb-4`}>
                <i className="bi bi-info-circle" aria-hidden="true" />
                {t("Dashboard.Platform.pricing.trialNote")}
              </p>

              <form onSubmit={submitTrialDays} className="flex flex-wrap items-end gap-3">
                <div>
                  <label className={formLabelClass} htmlFor="trial-days">
                    {t("Dashboard.Platform.pricing.trialDays")}
                  </label>
                  <input
                    id="trial-days"
                    type="number"
                    min="0"
                    max="90"
                    step="1"
                    className={`${formControlClass} w-32`}
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    data-testid="trial-days-input"
                  />
                </div>

                <button
                  type="submit"
                  className={btnPrimaryClass}
                  disabled={savingTrial}
                  data-testid="save-trial-days"
                >
                  {savingTrial
                    ? t("Dashboard.Platform.pricing.saving")
                    : t("Dashboard.Platform.pricing.saveTrialDays")}
                </button>

                {/* Zero is a real setting, and one worth spelling out — it reads
                    as an empty field rather than as a decision otherwise. */}
                {Number(trialDays) === 0 && (
                  <span className="text-xs font-semibold text-amber-700">
                    {t("Dashboard.Platform.pricing.trialDisabled")}
                  </span>
                )}
              </form>
            </div>
          </div>

          {/* --- Promotions ----------------------------------------------- */}
          <div className={`${cardClass} mb-6`}>
            <div className={cardHeaderClass}>
              <h2 className={cardTitleClass}>
                {t("Dashboard.Platform.pricing.promotionsTitle")}
              </h2>
              <button
                type="button"
                className={btnPrimaryClass}
                onClick={() => openPromotionEditor(null)}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                {t("Dashboard.Platform.pricing.newPromotion")}
              </button>
            </div>

            <div className={cardBodyClass}>
              {promotionForm && (
                <form
                  onSubmit={submitPromotion}
                  className="mb-5 rounded-card border border-brand-200 bg-brand-50/40 p-4"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={formLabelClass} htmlFor="promo-name">
                        {t("Dashboard.Platform.pricing.promotionName")}
                        <span className={requiredMarkClass}>*</span>
                      </label>
                      <input
                        id="promo-name"
                        type="text"
                        required
                        maxLength={100}
                        placeholder={t(
                          "Dashboard.Platform.pricing.promotionNamePlaceholder",
                        )}
                        className={formControlClass}
                        value={promotionForm.name}
                        onChange={(e) =>
                          setPromotionForm({
                            ...promotionForm,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className={formLabelClass} htmlFor="promo-percent">
                        {t("Dashboard.Platform.pricing.discountPercent")}
                        <span className={requiredMarkClass}>*</span>
                      </label>
                      <input
                        id="promo-percent"
                        type="number"
                        min="1"
                        max="100"
                        required
                        className={formControlClass}
                        value={promotionForm.discountPercent}
                        onChange={(e) =>
                          setPromotionForm({
                            ...promotionForm,
                            discountPercent: e.target.value,
                          })
                        }
                      />
                      <p className="mt-1 text-xs text-ink-400">
                        {t("Dashboard.Platform.pricing.discountHint")}
                      </p>
                    </div>

                    <div>
                      <label className={formLabelClass} htmlFor="promo-plan">
                        {t("Dashboard.Platform.pricing.appliesTo")}
                      </label>
                      <select
                        id="promo-plan"
                        className={formControlClass}
                        value={promotionForm.plan}
                        onChange={(e) =>
                          setPromotionForm({
                            ...promotionForm,
                            plan: e.target.value,
                          })
                        }
                      >
                        <option value="">
                          {t("Dashboard.Platform.pricing.allPlans")}
                        </option>
                        {PRICED_PLANS.map((plan) => (
                          <option key={plan} value={String(plan)}>
                            {planName(plan)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-ink-700">
                        <input
                          type="checkbox"
                          checked={promotionForm.isActive}
                          onChange={(e) =>
                            setPromotionForm({
                              ...promotionForm,
                              isActive: e.target.checked,
                            })
                          }
                        />
                        {t("Dashboard.Platform.pricing.active")}
                      </label>
                    </div>

                    <div>
                      <label className={formLabelClass} htmlFor="promo-start">
                        {t("Dashboard.Platform.pricing.startsAt")}
                        <span className={requiredMarkClass}>*</span>
                      </label>
                      <input
                        id="promo-start"
                        type="datetime-local"
                        required
                        className={formControlClass}
                        value={promotionForm.startsAt}
                        onChange={(e) =>
                          setPromotionForm({
                            ...promotionForm,
                            startsAt: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className={formLabelClass} htmlFor="promo-end">
                        {t("Dashboard.Platform.pricing.endsAt")}
                        <span className={requiredMarkClass}>*</span>
                      </label>
                      <input
                        id="promo-end"
                        type="datetime-local"
                        required
                        className={formControlClass}
                        value={promotionForm.endsAt}
                        onChange={(e) =>
                          setPromotionForm({
                            ...promotionForm,
                            endsAt: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      type="submit"
                      className={btnPrimaryClass}
                      disabled={savingPromotion}
                    >
                      {savingPromotion
                        ? t("Dashboard.Platform.pricing.saving")
                        : t("Dashboard.Platform.pricing.save")}
                    </button>
                    <button
                      type="button"
                      className={btnSecondaryClass}
                      onClick={() => setPromotionForm(null)}
                    >
                      {t("Dashboard.Platform.pricing.cancel")}
                    </button>
                  </div>
                </form>
              )}

              {promotions.length === 0 ? (
                <p className={alertInfoClass}>
                  <i className="bi bi-tag" aria-hidden="true" />
                  <span>{t("Dashboard.Platform.pricing.noPromotions")}</span>
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={tableHeadClass}>
                      <tr>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.promotionName")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.discountPercent")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.appliesTo")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.window")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.status")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.timesApplied")}
                        </th>
                        <th className={tableHeaderCellClass} />
                      </tr>
                    </thead>
                    <tbody className={tableBodyClass}>
                      {promotions.map((promotion) => (
                        <tr key={promotion.id} className={tableRowClass}>
                          <td
                            className={tableCellClass}
                            data-label={t(
                              "Dashboard.Platform.pricing.promotionName",
                            )}
                          >
                            {promotion.name}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t(
                              "Dashboard.Platform.pricing.discountPercent",
                            )}
                          >
                            −{promotion.discountPercent}%
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t(
                              "Dashboard.Platform.pricing.appliesTo",
                            )}
                          >
                            {planName(promotion.plan)}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t("Dashboard.Platform.pricing.window")}
                          >
                            {formatDateTime(promotion.startsAt)} —{" "}
                            {formatDateTime(promotion.endsAt)}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t("Dashboard.Platform.pricing.status")}
                          >
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                STATUS_BADGE[promotion.status] ||
                                "bg-ink-100 text-ink-500"
                              }`}
                            >
                              {t(
                                `Dashboard.Platform.pricing.statuses.${promotion.status}`,
                                { defaultValue: promotion.status },
                              )}
                            </span>
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t(
                              "Dashboard.Platform.pricing.timesApplied",
                            )}
                          >
                            {promotion.timesApplied}
                          </td>
                          <td className={tableActionsCellClass}>
                            <button
                              type="button"
                              className={btnSecondaryClass}
                              disabled={busyId === `promo-${promotion.id}`}
                              onClick={() => openPromotionEditor(promotion)}
                            >
                              {t("Dashboard.Platform.pricing.edit")}
                            </button>
                            <button
                              type="button"
                              className={btnSecondaryClass}
                              disabled={busyId === `promo-${promotion.id}`}
                              onClick={() => togglePromotion(promotion)}
                            >
                              {promotion.isActive
                                ? t("Dashboard.Platform.pricing.disable")
                                : t("Dashboard.Platform.pricing.enable")}
                            </button>
                            {/* Only offered while nothing was sold under it —
                                the API refuses otherwise, and the record of why
                                a customer's price is what it is has to stay. */}
                            {promotion.timesApplied === 0 && (
                              <button
                                type="button"
                                className={btnDangerClass}
                                disabled={busyId === `promo-${promotion.id}`}
                                onClick={() => removePromotion(promotion)}
                              >
                                {t("Dashboard.Platform.pricing.delete")}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* --- Price history -------------------------------------------- */}
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <h2 className={cardTitleClass}>
                {t("Dashboard.Platform.pricing.historyTitle")}
              </h2>
            </div>
            <div className={cardBodyClass}>
              {history.length === 0 ? (
                <p className={alertInfoClass}>
                  <i className="bi bi-clock-history" aria-hidden="true" />
                  <span>{t("Dashboard.Platform.pricing.noHistory")}</span>
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className={tableClass}>
                    <thead className={tableHeadClass}>
                      <tr>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.plan")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.amount")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.effectiveFrom")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.note")}
                        </th>
                        <th className={tableHeaderCellClass}>
                          {t("Dashboard.Platform.pricing.setBy")}
                        </th>
                        <th className={tableHeaderCellClass} />
                      </tr>
                    </thead>
                    <tbody className={tableBodyClass}>
                      {history.map((version) => (
                        <tr key={version.id} className={tableRowClass}>
                          <td
                            className={tableCellClass}
                            data-label={t("Dashboard.Platform.pricing.plan")}
                          >
                            {planName(version.plan)}
                            {version.isCurrent && (
                              <span className="ml-2 inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700">
                                {t("Dashboard.Platform.pricing.statuses.live")}
                              </span>
                            )}
                            {!version.isEffective && (
                              <span className="ml-2 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                {t(
                                  "Dashboard.Platform.pricing.statuses.scheduled",
                                )}
                              </span>
                            )}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t("Dashboard.Platform.pricing.amount")}
                          >
                            {formatMoney(version.price, version.currency)}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t(
                              "Dashboard.Platform.pricing.effectiveFrom",
                            )}
                          >
                            {formatDateTime(version.effectiveFrom)}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t("Dashboard.Platform.pricing.note")}
                          >
                            {version.note || "—"}
                          </td>
                          <td
                            className={tableCellClass}
                            data-label={t("Dashboard.Platform.pricing.setBy")}
                          >
                            {version.createdByName || "—"}
                          </td>
                          <td className={tableActionsCellClass}>
                            {/* A price that has already applied is the record of
                                what somebody was sold at, so only a scheduled
                                one can be taken back. */}
                            {!version.isEffective && (
                              <button
                                type="button"
                                className={btnDangerClass}
                                disabled={busyId === `price-${version.id}`}
                                onClick={() => withdrawPrice(version)}
                              >
                                {t("Dashboard.Platform.pricing.withdraw")}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlatformPricing;
